import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Clock } from "lucide-react";
import { Reveal, PageHero } from "@/components/shared";
import Newsletter from "@/components/Newsletter";
import { API } from "@/lib/api";
import { IMAGES } from "@/lib/content";

export default function Insights() {
  const [articles, setArticles] = useState([]);

  useEffect(() => {
    API.get("/articles").then(({ data }) => setArticles(data)).catch(() => {});
  }, []);

  const [featured, ...rest] = articles;

  return (
    <main data-testid="insights-page">
      <PageHero
        eyebrow="Insights"
        title="Perspective on Caribbean commerce, tech & hospitality."
        lead="Short, practical reads for island business owners. Outcomes first, jargon never."
        image={IMAGES.dining}
      />

      <section className="pb-24 lg:pb-32">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          {featured && (
            <Reveal>
              <Link
                to={`/insights/${featured.slug}`}
                data-testid="featured-article-card"
                className="gold-ring-card group grid overflow-hidden lg:grid-cols-2"
              >
                <div className="relative h-72 overflow-hidden lg:h-auto">
                  <img src={featured.cover_image} alt={featured.title} className="h-full w-full object-cover duotone transition-transform duration-700 group-hover:scale-105" />
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent to-navy-surface/40" />
                </div>
                <div className="flex flex-col justify-center p-8 lg:p-12">
                  <span className="font-mono text-[11px] uppercase tracking-[0.25em] text-gold-glow">Featured insight</span>
                  <h2 className="mt-4 font-serif text-2xl leading-snug text-ivory sm:text-3xl">{featured.title}</h2>
                  <p className="mt-4 text-sm font-light leading-relaxed text-slate-400">{featured.excerpt}</p>
                  <div className="mt-6 flex items-center gap-5 text-xs text-slate-500">
                    <span>{featured.author}</span>
                    <span className="flex items-center gap-1.5"><Clock className="h-3.5 w-3.5" /> {featured.read_time}</span>
                  </div>
                  <span className="mt-6 inline-flex items-center gap-2 text-sm font-medium text-gold-glow">
                    Read the article <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                  </span>
                </div>
              </Link>
            </Reveal>
          )}

          <div className="mt-10 grid gap-6 md:grid-cols-3">
            {rest.map((a, i) => (
              <Reveal key={a.id} delay={i * 0.1}>
                <Link to={`/insights/${a.slug}`} data-testid={`article-card-${a.slug}`} className="gold-ring-card group flex h-full flex-col overflow-hidden">
                  <div className="relative h-48 overflow-hidden">
                    <img src={a.cover_image} alt={a.title} className="h-full w-full object-cover duotone transition-transform duration-700 group-hover:scale-105" />
                    <div className="absolute inset-0 bg-gradient-to-t from-navy-surface to-transparent" />
                  </div>
                  <div className="flex flex-1 flex-col p-7">
                    <h3 className="font-serif text-xl leading-snug text-ivory">{a.title}</h3>
                    <p className="mt-3 flex-1 text-sm font-light leading-relaxed text-slate-400">{a.excerpt}</p>
                    <div className="mt-5 flex items-center gap-4 text-xs text-slate-500">
                      <span className="flex items-center gap-1.5"><Clock className="h-3.5 w-3.5" /> {a.read_time}</span>
                    </div>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="border-t border-gold/10 bg-navy-deep/50 py-24" data-testid="insights-newsletter">
        <div className="mx-auto max-w-2xl px-6 text-center lg:px-10">
          <Reveal>
            <p className="eyebrow">The Caribbean Digital Dispatch</p>
            <h2 className="mt-4 font-serif text-3xl tracking-tight text-ivory sm:text-4xl">One email a month. Worth opening.</h2>
            <p className="mt-4 text-sm font-light text-slate-400">What is working in Caribbean digital right now, written for owners — not engineers.</p>
            <div className="mt-8"><Newsletter /></div>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
